// Site-wide "log in and get approved before you can play" gate, shared
// across every Around Table Round game (Thunderstoner, Space Base, and
// whatever comes after) via one RTDB key - `siteUsers/{uid}` - in the
// Firebase project this app shares with them (see CLAUDE.md's "Firebase"
// section). One approval covers every game: sign in once here or on
// Space Base, get approved once, and you're in everywhere.
//
// Matt: "I would like to have people who come to the site[...] need to
// log in, and that I need to be able to approve their registration
// before they can access any of the games[...] give approval to me and
// Brian[...] I'd like to get an email or some kind of prompt when
// somebody asks to join, and then I can just[...] allow them."
//
// The database.rules.json ".write" rule is what actually enforces all of
// this (a user can only ever create their OWN record as
// status:'pending', can update their own non-status/isAdmin fields, and
// can never self-approve or self-admin) - the client-side logic here is
// just "ask nicely" convenience on top of a security boundary that's
// fully server-side. One exception is carved out AT THE RULE LEVEL, not
// here: a first-time sign-in whose verified Google email is
// mattgrosso@gmail.com is allowed to write itself in as an approved
// admin directly - see the rule's own comment for why (bootstrapping the
// very first admin without needing anyone's uid known in advance).
import {
  ref as dbRef, onValue, get, set, update,
} from 'firebase/database';
import { db } from '../firebase';

const ADMIN_BOOTSTRAP_EMAIL = 'mattgrosso@gmail.com';

// Offline fallback for the approval gate (Matt: wants local hotseat/vs-AI
// play to work with zero connectivity, e.g. on a flight, after having
// visited the site once while online). watchSiteUserStatus's onValue
// subscription (below) simply never resolves without a network - there's
// no built-in offline cache for RTDB the way Firestore has one - so
// store/index.js's initAuth caches the last CONFIRMED-approved record here
// every time the live subscription reports one, and falls back to it if
// the live subscription hasn't answered within a short timeout or the
// browser already knows it's offline. Deliberately keyed by uid (not a
// bare boolean) so a cached approval can only ever unlock the app for the
// exact account it was cached for - see readCachedSiteUser's own uid
// check. Deliberately NEVER written for 'pending'/'denied' (see
// store/index.js's own call site) - there's no user-hostile outcome from
// failing safe toward "still shows the waiting/sign-in screen" for those,
// unlike wrongly unlocking gameplay for someone who was never actually
// approved.
const SITE_USER_CACHE_KEY = 'spacebase:approvedSiteUser:v1';

export function cacheApprovedSiteUser(uid, record) {
  if (!uid || record?.status !== 'approved') return;
  try {
    localStorage.setItem(SITE_USER_CACHE_KEY, JSON.stringify({
      uid, status: 'approved', isAdmin: Boolean(record.isAdmin), cachedAt: Date.now(),
    }));
  } catch {
    // Storage full/unavailable - the live subscription is still the
    // primary path; losing the fallback cache just means a future offline
    // launch can't use it, not a crash worth causing now.
  }
}

// Returns the cached record only if it's for THIS uid - a cached approval
// for a previous account signed into this same browser must never unlock
// the app for whoever's signed in now.
export function readCachedSiteUser(uid) {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(SITE_USER_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (cached.uid !== uid || cached.status !== 'approved') return null;
    return cached;
  } catch {
    return null;
  }
}

// Ensures `siteUsers/{uid}` exists for the just-signed-in user, creating
// it if this is their first time. Safe to call every sign-in - a no-op
// once the record exists (the security rule wouldn't allow overwriting
// status/isAdmin here anyway, so this never "un-approves" anyone).
export async function ensureSiteUserRecord(user) {
  if (!db || !user) return;
  const userRef = dbRef(db, `siteUsers/${user.uid}`);
  const snapshot = await get(userRef);
  if (snapshot.exists()) {
    // Keep displayName/email fresh (their own record, always writable).
    await update(userRef, { email: user.email, displayName: user.displayName || null });
    return;
  }
  const isBootstrapAdmin = user.email === ADMIN_BOOTSTRAP_EMAIL;
  await set(userRef, {
    email: user.email,
    displayName: user.displayName || null,
    status: isBootstrapAdmin ? 'approved' : 'pending',
    isAdmin: isBootstrapAdmin,
    requestedAt: Date.now(),
  });
}

// Live-subscribes to the current user's own siteUsers record so a
// pending user's screen unblocks the moment an admin approves them,
// without needing a manual refresh. Returns an unsubscribe function.
export function watchSiteUserStatus(uid, callback) {
  if (!db || !uid) {
    callback(null);
    return () => {};
  }
  const userRef = dbRef(db, `siteUsers/${uid}`);
  return onValue(userRef, (snapshot) => callback(snapshot.val()));
}

// Admin-only (enforced server-side by the same rule) - the full pending/
// approved/denied roster, live.
export function watchAllSiteUsers(callback) {
  if (!db) {
    callback({});
    return () => {};
  }
  const usersRef = dbRef(db, 'siteUsers');
  return onValue(usersRef, (snapshot) => callback(snapshot.val() || {}), () => callback({}));
}

export async function setSiteUserStatus(uid, status) {
  if (!db) return;
  await update(dbRef(db, `siteUsers/${uid}`), { status });
}
